// @ts-strict-ignore
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Divider from 'antd/lib/divider';
import { dayjs } from '@/extendedDayjs';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { DatetimeFormat } from '../../timeHelpers';
import { CommitItem, FeatureItem, PRItem, PullRequestStatus, mapCodeStatus } from '../../types';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import { codeIsCommit, mergeQueueStateForCodeItem, tagColorForFeatureItemCodeStatus } from '../../helpers';
import Tag from 'antd/lib/tag';
import { displayCodeStatusForStatus } from '../../helpers/displayHelpers';
import { iconForStatus, stackInfoTooltipText } from './featureReadinessHelpers';
import { ThunderboltFilled } from '@ant-design/icons';
import StackedIcon from '../icons/StackedIcon';
import { RootState } from '../../reducers/reducers';
import { selectEffectiveTimezone } from '../../selectors/selectors';

interface CodeStatusProps {
    code?: PRItem | CommitItem;
    ignored: boolean;
    workingBranchEnabled: boolean;
    shouldShowInOneRow?: boolean;
    appId: string;
    isAutoMergeRunwayPRsEnabled: boolean;
    automationTooltipMessage?: string;
    groupedCodeItems?: FeatureItem[];
}

const CodeStatus: React.FC<CodeStatusProps> = (props: CodeStatusProps) => {
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    if (props.groupedCodeItems) {
        return (
            <div>
                <Tooltip
                    title={`There are ${props.groupedCodeItems.length} code items associated with this ticket. Click to open the item's detail drawer for info on the code items.`}
                >
                    <Text style={{ whiteSpace: 'nowrap' }}>
                        There are {props.groupedCodeItems.length} code items associated with this ticket.
                    </Text>
                </Tooltip>
            </div>
        );
    }

    if (!props.code) {
        return (
            <div>
                <Tooltip title="We have not detected any code associated with this ticket.">
                    <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                        No code found
                    </Text>
                </Tooltip>
            </div>
        );
    }

    const code = props.code;

    const status = mapCodeStatus(code.status);
    const mergeQueueState = mergeQueueStateForCodeItem(code);
    const bgColor = tagColorForFeatureItemCodeStatus(status, mergeQueueState);
    const identifier = code.identifier;

    let itemDate;
    if (codeIsCommit(code)) {
        itemDate = code.committedAt;
    } else {
        itemDate = (code as PRItem).updatedAt ?? (code as PRItem).createdAt;
    }

    const localTimezone = dayjs.tz.guess();
    const isLocalTimezone = effectiveTimezone === localTimezone;
    const shortTz =
        effectiveTimezone && !isLocalTimezone ? ` ${dayjs.tz(dayjs(itemDate), effectiveTimezone).format('z')}` : '';
    const formattedDate =
        itemDate ?
            dayjs(itemDate).tz(effectiveTimezone).format(DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated) +
            shortTz
        :   null;
    const url = code.url;
    let codeIdentifierComponent = null;

    if (url) {
        let title = null;
        if (codeIsCommit(code)) {
            title = `${identifier}`;
        } else {
            title = `#${identifier}`;
        }

        codeIdentifierComponent = (
            <Link
                href={url}
                target="_blank"
                style={{ whiteSpace: 'nowrap' }}
                onClick={(e) => e.stopPropagation()} //stop drawer from opening
                strong
            >
                {title}
            </Link>
        );
    }

    const stackInfo = code.stackInfo;

    let branch;
    let targetedBranch;
    if (codeIsCommit(code)) {
        branch = code.branch;
    } else {
        const prItem = code as PRItem;
        if (prItem.pullRequestStatus === PullRequestStatus.Open) {
            branch = stackInfo?.baseBranch ?? prItem.baseBranch;
            if (stackInfo && stackInfo.baseBranch !== prItem.baseBranch) {
                targetedBranch = prItem.baseBranch;
            }
        } else {
            // headBranch could be empty on legacy items (and their baseBranch should be set to correct value), so fall back gracefully
            branch = prItem.headBranch != '' ? prItem.headBranch : prItem.baseBranch;
        }
    }

    const stackInfoComponent =
        stackInfo ?
            <Tooltip title={stackInfoTooltipText(stackInfo, targetedBranch)}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <StackedIcon width={14} height={14} />
                    {stackInfo.position > 0 && stackInfo.size > 0 && (
                        <Text style={{ fontFamily: 'monospace', fontSize: 12, marginLeft: 8 }} ellipsis>
                            {stackInfo.position}/{stackInfo.size}
                        </Text>
                    )}
                </div>
            </Tooltip>
        :   null;

    const branchComponent = (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {iconForStatus(status, code)}
            <Text style={{ fontFamily: 'monospace', fontSize: 12, marginLeft: 8 }} ellipsis>
                {branch}
            </Text>
        </div>
    );

    return (
        <Space direction={props.shouldShowInOneRow ? 'horizontal' : 'vertical'} size={10} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={formattedDate ? `Updated on ${formattedDate}` : null}>
                    <Tag color={bgColor} style={{ ...tagStyle, fontSize: 10, marginInlineEnd: 4 }}>
                        {displayCodeStatusForStatus(status, props.workingBranchEnabled, mergeQueueState)}
                    </Tag>
                </Tooltip>
                {props.automationTooltipMessage && (
                    <Tooltip title={props.automationTooltipMessage}>
                        <ThunderboltFilled style={{ color: runwayColors.info }} />
                    </Tooltip>
                )}
            </div>
            {branch && (
                <Space
                    align="center"
                    split={
                        <Divider
                            type="vertical"
                            style={{ backgroundColor: antGrey.grey5, marginLeft: 2, marginRight: 2, height: 14 }}
                        />
                    }
                    style={{ width: '100%' }}
                    className="space-code-ticket-status"
                >
                    {[codeIdentifierComponent, stackInfoComponent, branchComponent].filter(Boolean)}
                </Space>
            )}
        </Space>
    );
};

export default CodeStatus;
